import * as ChartTypes from 'reporting-data/constants/chartTypes';
import { isRelationalReport } from 'reporting-data/tsTypes/reportTypes';
import { gate } from 'hub-http/gates';
import { Map as ImmutableMap } from 'immutable';
import { getReportChartType } from 'reporting-data/report/reportGetters';
import { RelationalReport } from 'reporting-snowflake/relational/schema/report-records';
//@ts-expect-error untyped dependency
import { getGrandTotalColumn } from 'reporting-snowflake/relational/visualize/visual-mappers/pie-mapper';
import { getReportColorEncoding, getReportValuesEncodings } from 'reporting-snowflake/relational/utils/report-utils';
const SLICED_MIGRATION_GATE = gate('ReportingViz:UnifiedSlicedSFMigration');
const canMigrate = (report, gates) => {
  var _gates$includes;
  const chartType = getReportChartType(report);
  const isCompatibleChartType = [ChartTypes.PIE, ChartTypes.DONUT].includes(chartType);
  const isUngated = (_gates$includes = gates === null || gates === void 0 ? void 0 : gates.includes(SLICED_MIGRATION_GATE)) !== null && _gates$includes !== void 0 ? _gates$includes : false;
  return isUngated && isRelationalReport(report) && isCompatibleChartType;
};
const getColorFormattingOptions = report => {
  const color = getReportColorEncoding(report);
  return color === null || color === void 0 ? void 0 : color.options;
};
const getValueFormattingOptions = report => {
  const values = getReportValuesEncodings(report);
  const valueOptionsByColumn = values.map(v => {
    return {
      column: v.column.get('alias'),
      options: v.options.toJS()
    };
  });
  return valueOptionsByColumn;
};

/**
 * Transformation Rules:
 * 1. Set `chartType` to `reportDefinition.visual.type`.
 * 2. Set `color` to `reportDefinition.visual.options.color`.
 * 3. Set `style.dataLabels` to `reportDefinition.visual.options.showDataLabels`.
 * 4. Set `style.showTotals` to `reportDefinition.visual.options.showTotals`.
 * 5. Set `style.legendPosition` to `reportDefinition.visual.options.legendPosition`.
 * 6. Set `style.showRecordId` to `reportDefinition.visual.options.showRecordId`.
 * 7. Set `layer.grandTotalColumn` to the grand total column derived from reportDefinition.
 * 8. Set `layer.colorFormattingOptions` to the color formatting options derived from reportDefinition.
 * 9. Set `layer.valueFormattingOptions` to the value formatting options derived from reportDefinition.
 * 10. Delete `reportDefinition.visual.options`.
 */
const migrate = (reportInput, gates) => {
  if (!isRelationalReport(reportInput) || !canMigrate(reportInput, gates)) {
    return reportInput;
  }
  const migratedReport = reportInput.withMutations(map => {
    const reportDefinition = map.get('reportDefinition');
    const relationalReportRecord = RelationalReport(reportDefinition);
    const visual = reportDefinition.get('visual');
    const visualType = visual.get('type');
    const options = visual.get('options') || ImmutableMap();
    const grandTotalColumn = getGrandTotalColumn(relationalReportRecord);
    const colorFormattingOptions = getColorFormattingOptions(relationalReportRecord);
    const valueFormattingOptions = getValueFormattingOptions(relationalReportRecord);
    const chart = ImmutableMap({
      chartType: visualType,
      color: options.get('color'),
      style: ImmutableMap({
        dataLabels: options.get('showDataLabels'),
        showTotals: options.get('showTotals'),
        legendPosition: options.get('legendPosition'),
        showRecordId: options.get('showRecordId')
      }),
      layer: ImmutableMap({
        grandTotalColumn,
        colorFormattingOptions,
        valueFormattingOptions
      })
    });
    return map.set('chart', chart).deleteIn(['reportDefinition', 'visual', 'options']);
  });
  return migratedReport;
};
export const SnowflakeSlicedMigration = {
  canMigrate,
  migrate
};
export const __TESTABLE__ = {
  SLICED_MIGRATION_GATE
};